<script setup lang="ts">
import type { SelectItem } from "@/types";

interface Props {
  searchQuery?: string;
  location?: string;
  capacity?: SelectItem;
  experience?: SelectItem;
  workingModel?: SelectItem;
}

const props = defineProps<Props>();

const { candidateRegisterUrl } = useCandidateUrls();

// Generate dynamic message based on filters
const noJobsMessage = computed(() => {
  const parts: string[] = [];

  // Employment type (capacity) - e.g., "full-time", "part-time"
  if (props.capacity) {
    parts.push(props.capacity.name.toLowerCase());
  }

  // Working model - e.g., "remote", "hybrid"
  if (props.workingModel) {
    parts.push(props.workingModel.name.toLowerCase());
  }

  if (props.searchQuery) {
    parts.push(props.searchQuery.toLowerCase());
  }

  // Base sentence with job type
  let sentence = "Sorry, we couldn't find any";

  if (parts.length > 0) {
    sentence += ` ${parts.join(" ")}`;
  }

  sentence += " jobs";

  // Add experience level qualifier if present
  if (props.experience) {
    sentence += ` for candidates with ${props.experience.name.toLowerCase()}`;
  }

  // Add location qualifier if present
  if (props.location) {
    sentence += ` in ${props.location}`;
  }

  sentence += ".";

  return sentence;
});

const handleRegisterEmployer = () => {
  navigateTo(ROUTES.register);
};

const handleRegisterCandidate = () => {
  navigateTo(candidateRegisterUrl, { external: true, open: { target: "_blank" } });
};
</script>

<template>
  <div class="py-12 2xl:py-16 3xl:py-20">
    <div class="max-w-5xl 2xl:max-w-6xl mx-auto">
      <!-- Main empty state message -->
      <div class="text-center mb-12 2xl:mb-16">
        <div class="w-20 h-20 2xl:w-24 2xl:h-24 3xl:w-28 3xl:h-28 rounded-3xl bg-sand/50 flex items-center justify-center mx-auto mb-6 2xl:mb-8">
          <Icon name="material-symbols:search-off" class="w-10 h-10 2xl:w-12 2xl:h-12 3xl:w-14 3xl:h-14 text-medium-gray" />
        </div>
        <h3 class="font-secondary text-2xl 2xl:text-3xl 3xl:text-4xl font-bold text-dark-gray mb-3 2xl:mb-4">{{ noJobsMessage }}</h3>
        <p class="text-medium-gray text-base 2xl:text-lg 3xl:text-xl max-w-lg mx-auto">
          Try adjusting your search or filters, or explore the options below to get started.
        </p>
      </div>

      <!-- Two column CTAs -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 2xl:gap-8 3xl:gap-10">
        <!-- Candidate CTA -->
        <div class="group bg-white rounded-3xl 3xl:rounded-[2rem] border-2 border-sand/50 shadow-warm hover:shadow-warm-lg hover:border-primary/30 transition-all duration-300 overflow-hidden">
          <!-- Header -->
          <div class="bg-gradient-to-r from-primary/5 via-secondary/5 to-savanna/5 p-6 2xl:p-8 3xl:p-10 border-b border-sand/50">
            <div class="flex items-center gap-4 2xl:gap-5">
              <div class="w-14 h-14 2xl:w-16 2xl:h-16 3xl:w-20 3xl:h-20 rounded-2xl 2xl:rounded-3xl bg-gradient-to-br from-primary to-primary-light flex items-center justify-center">
                <Icon name="material-symbols:person-search" class="w-7 h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 text-white" />
              </div>
              <div>
                <p class="text-primary text-sm 2xl:text-base font-medium">For Job Seekers</p>
                <h4 class="font-secondary font-bold text-dark-gray text-xl 2xl:text-2xl 3xl:text-3xl">Looking for a job?</h4>
              </div>
            </div>
          </div>

          <!-- Body -->
          <div class="p-6 2xl:p-8 3xl:p-10">
            <p class="text-medium-gray text-base 2xl:text-lg 3xl:text-xl leading-relaxed mb-6 2xl:mb-8">
              Create your profile to get matched with exclusive opportunities and connect with top employers in hospitality.
            </p>

            <ul class="space-y-3 2xl:space-y-4 mb-6 2xl:mb-8">
              <li class="flex items-center gap-3">
                <div class="w-6 h-6 2xl:w-7 2xl:h-7 rounded-lg bg-savanna/10 flex items-center justify-center">
                  <Icon name="material-symbols:check" class="w-4 h-4 2xl:w-5 2xl:h-5 text-savanna" />
                </div>
                <span class="text-dark-gray text-sm 2xl:text-base">100% free to use</span>
              </li>
              <li class="flex items-center gap-3">
                <div class="w-6 h-6 2xl:w-7 2xl:h-7 rounded-lg bg-savanna/10 flex items-center justify-center">
                  <Icon name="material-symbols:check" class="w-4 h-4 2xl:w-5 2xl:h-5 text-savanna" />
                </div>
                <span class="text-dark-gray text-sm 2xl:text-base">AI-powered job matching</span>
              </li>
              <li class="flex items-center gap-3">
                <div class="w-6 h-6 2xl:w-7 2xl:h-7 rounded-lg bg-savanna/10 flex items-center justify-center">
                  <Icon name="material-symbols:check" class="w-4 h-4 2xl:w-5 2xl:h-5 text-savanna" />
                </div>
                <span class="text-dark-gray text-sm 2xl:text-base">Get noticed with video bios</span>
              </li>
            </ul>

            <button
              @click="handleRegisterCandidate"
              class="w-full group/btn px-8 py-4 2xl:px-10 2xl:py-5 rounded-full h-btn-sunset shadow-sunset hover:shadow-lg transition-all duration-300 motion-safe:hover:scale-105"
            >
              <span class="flex items-center justify-center text-base 2xl:text-lg font-semibold text-white">
                Create Your Profile
                <Icon name="material-symbols:arrow-forward" class="ml-2 w-5 h-5 2xl:w-6 2xl:h-6 group-hover/btn:translate-x-1 transition-transform duration-300" />
              </span>
            </button>
          </div>
        </div>

        <!-- Employer CTA -->
        <div class="group bg-white rounded-3xl 3xl:rounded-[2rem] border-2 border-sand/50 shadow-warm hover:shadow-warm-lg hover:border-secondary/30 transition-all duration-300 overflow-hidden">
          <!-- Header -->
          <div class="bg-gradient-to-r from-secondary/5 via-terracotta/5 to-primary/5 p-6 2xl:p-8 3xl:p-10 border-b border-sand/50">
            <div class="flex items-center gap-4 2xl:gap-5">
              <div class="w-14 h-14 2xl:w-16 2xl:h-16 3xl:w-20 3xl:h-20 rounded-2xl 2xl:rounded-3xl bg-gradient-to-br from-secondary to-secondary-light flex items-center justify-center">
                <Icon name="material-symbols:add-business" class="w-7 h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 text-white" />
              </div>
              <div>
                <p class="text-secondary text-sm 2xl:text-base font-medium">For Employers</p>
                <h4 class="font-secondary font-bold text-dark-gray text-xl 2xl:text-2xl 3xl:text-3xl">Are you hiring?</h4>
              </div>
            </div>
          </div>

          <!-- Body -->
          <div class="p-6 2xl:p-8 3xl:p-10">
            <p class="text-medium-gray text-base 2xl:text-lg 3xl:text-xl leading-relaxed mb-6 2xl:mb-8">
              Post your job opening and connect with thousands of qualified hospitality candidates actively searching.
            </p>

            <ul class="space-y-3 2xl:space-y-4 mb-6 2xl:mb-8">
              <li class="flex items-center gap-3">
                <div class="w-6 h-6 2xl:w-7 2xl:h-7 rounded-lg bg-terracotta/10 flex items-center justify-center">
                  <Icon name="material-symbols:check" class="w-4 h-4 2xl:w-5 2xl:h-5 text-terracotta" />
                </div>
                <span class="text-dark-gray text-sm 2xl:text-base">Post your first job free</span>
              </li>
              <li class="flex items-center gap-3">
                <div class="w-6 h-6 2xl:w-7 2xl:h-7 rounded-lg bg-terracotta/10 flex items-center justify-center">
                  <Icon name="material-symbols:check" class="w-4 h-4 2xl:w-5 2xl:h-5 text-terracotta" />
                </div>
                <span class="text-dark-gray text-sm 2xl:text-base">Screen candidates with video</span>
              </li>
              <li class="flex items-center gap-3">
                <div class="w-6 h-6 2xl:w-7 2xl:h-7 rounded-lg bg-terracotta/10 flex items-center justify-center">
                  <Icon name="material-symbols:check" class="w-4 h-4 2xl:w-5 2xl:h-5 text-terracotta" />
                </div>
                <span class="text-dark-gray text-sm 2xl:text-base">AI-powered matching</span>
              </li>
            </ul>

            <button
              @click="handleRegisterEmployer"
              class="w-full group/btn px-8 py-4 2xl:px-10 2xl:py-5 rounded-full bg-gradient-to-r from-secondary to-secondary-light text-white shadow-sunset hover:shadow-lg transition-all duration-300 motion-safe:hover:scale-105"
            >
              <span class="flex items-center justify-center text-base 2xl:text-lg font-semibold">
                Post a Job
                <Icon name="material-symbols:arrow-forward" class="ml-2 w-5 h-5 2xl:w-6 2xl:h-6 group-hover/btn:translate-x-1 transition-transform duration-300" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
